import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useFormik} from 'formik'
import {PUBLIC_COMPANY_PATH} from '../../public/PublicModels'
import {
  ICorporateModel,
  ICorporateRequest,
  initialCorporateForm,
  slugify,
  toCorporateForm,
  validationCorporate,
} from './CorporateModel'

type Props = {
  show: boolean
  editingItem: ICorporateModel | null
  loading: boolean
  onClose: () => void
  onSubmit: (request: ICorporateRequest) => void
}

export const CorporateFormModal: FC<Props> = ({show, editingItem, loading, onClose, onSubmit}) => {
  const isEditing = !!editingItem
  // Yeni kayıtta sayfa adresi elle değiştirilene kadar firma adından üretilir
  const [slugTouched, setSlugTouched] = useState(false)

  const formik = useFormik<ICorporateRequest>({
    initialValues: initialCorporateForm,
    validationSchema: validationCorporate,
    onSubmit: (values) =>
      onSubmit({
        name: values.name.trim(),
        slug: values.slug,
        taxNumber: values.taxNumber.trim(),
        taxOffice: values.taxOffice.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        address: values.address.trim(),
      }),
  })

  useEffect(() => {
    if (!show) return
    formik.resetForm({values: editingItem ? toCorporateForm(editingItem) : initialCorporateForm})
    setSlugTouched(!!editingItem?.slug)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingItem])

  const fieldError = (field: keyof ICorporateRequest) =>
    formik.touched[field] && formik.errors[field] ? formik.errors[field] : undefined

  const inputClass = (field: keyof ICorporateRequest) =>
    `form-control ${fieldError(field) ? 'is-invalid' : ''}`

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event)
    if (!slugTouched) formik.setFieldValue('slug', slugify(event.target.value))
  }

  const renderText = (field: keyof ICorporateRequest, label: string, placeholder = '', required = false) => (
    <>
      <label className={`form-label fw-bold ${required ? 'required' : ''}`}>{label}</label>
      <input type='text' className={inputClass(field)} placeholder={placeholder} {...formik.getFieldProps(field)} />
      {fieldError(field) && <div className='invalid-feedback'>{fieldError(field)}</div>}
    </>
  )

  return (
    <Modal show={show} onHide={onClose} size='lg' dialogClassName='modal-dialog-centered' backdrop='static'>
      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='modal-header'>
          <h5 className='modal-title'>{isEditing ? 'Firma Düzenle' : 'Yeni Firma'}</h5>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>

        <div className='modal-body'>
          <div className='row g-5'>
            <div className='col-md-6'>
              <label className='form-label fw-bold required'>Firma Adı</label>
              <input
                type='text'
                autoFocus
                name='name'
                className={inputClass('name')}
                placeholder='Örn: Eywa Teknoloji A.Ş.'
                value={formik.values.name}
                onChange={handleNameChange}
                onBlur={formik.handleBlur}
              />
              {fieldError('name') && <div className='invalid-feedback'>{fieldError('name')}</div>}
            </div>

            <div className='col-md-6'>
              <label className='form-label fw-bold required'>Kariyer Sayfası Adresi</label>
              <div className='input-group'>
                <span className='input-group-text'>{PUBLIC_COMPANY_PATH}/</span>
                <input
                  type='text'
                  name='slug'
                  className={inputClass('slug')}
                  placeholder='eywa-teknoloji'
                  value={formik.values.slug}
                  onChange={(event) => {
                    setSlugTouched(true)
                    formik.setFieldValue('slug', event.target.value.toLowerCase())
                  }}
                  onBlur={formik.handleBlur}
                />
                {fieldError('slug') && <div className='invalid-feedback'>{fieldError('slug')}</div>}
              </div>
            </div>

            <div className='col-md-6'>{renderText('taxNumber', 'Vergi Numarası')}</div>
            <div className='col-md-6'>{renderText('taxOffice', 'Vergi Dairesi')}</div>
            <div className='col-md-6'>{renderText('email', 'E-posta', 'info@firma.com')}</div>
            <div className='col-md-6'>{renderText('phone', 'Telefon', '0212 000 00 00')}</div>

            <div className='col-12'>
              <label className='form-label fw-bold'>Adres</label>
              <textarea rows={2} className={inputClass('address')} {...formik.getFieldProps('address')} />
              {fieldError('address') && <div className='invalid-feedback'>{fieldError('address')}</div>}
            </div>
          </div>
        </div>

        <div className='modal-footer'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            İptal
          </button>
          <button type='submit' className='btn btn-primary' disabled={loading}>
            {loading ? (
              <span className='indicator-progress' style={{display: 'block'}}>
                Lütfen bekleyin...
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            ) : isEditing ? (
              'Güncelle'
            ) : (
              'Kaydet'
            )}
          </button>
        </div>
      </form>
    </Modal>
  )
}
